import type { FC } from "react";
import { Button, Card, Form, Progress } from "antd";
import StyledPasswordInput from "src/components/reusable/StyledPasswordInput";
import type { TFunction } from "i18next";
import type { PasswordStrength } from "../Types/AccountTypes";

interface AccountPasswordCardProps {
  t: TFunction;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  passwordStrength: PasswordStrength;
  isUpdatingPassword: boolean;
  onCurrentPasswordChange: (_value: string) => void;
  onNewPasswordChange: (_value: string) => void;
  onConfirmPasswordChange: (_value: string) => void;
  onPasswordChange: () => void;
}

const AccountPasswordCard: FC<AccountPasswordCardProps> = ({
  t,
  currentPassword,
  newPassword,
  confirmPassword,
  passwordError,
  passwordStrength,
  isUpdatingPassword,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onPasswordChange,
}) => (
  <Card title={t("accountSettings.changePasswordTitle")} className="password-card">
    <Form layout="vertical" className="password-form">
      <Form.Item label={t("accountSettings.labels.currentPassword")}>
        <StyledPasswordInput
          value={currentPassword}
          onChange={e => onCurrentPasswordChange(e.target.value)}
        />
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.newPassword")}>
        <StyledPasswordInput
          value={newPassword}
          onChange={e => onNewPasswordChange(e.target.value)}
        />
        {passwordStrength.text && (
          <div className="password-strength">
            <Progress
              percent={passwordStrength.percent}
              status={passwordStrength.status as "success" | "exception" | "normal" | "active"}
              showInfo={false}
            />
            <span className={`strength-text ${passwordStrength.status}`}>
              {passwordStrength.text}
            </span>
          </div>
        )}
        {passwordError && <div className="error-message">{passwordError}</div>}
      </Form.Item>

      <Form.Item label={t("accountSettings.labels.confirmPassword")}>
        <StyledPasswordInput
          value={confirmPassword}
          onChange={e => onConfirmPasswordChange(e.target.value)}
        />
      </Form.Item>

      <Button
        type="primary"
        className="button button--primary setting-btn"
        onClick={onPasswordChange}
        loading={isUpdatingPassword}
      >
        {isUpdatingPassword
          ? t("accountSettings.updating")
          : t("accountSettings.updatePassword")}
      </Button>
    </Form>
  </Card>
);

export default AccountPasswordCard;
